import { useState } from 'react'
import { Card } from '@magnetic/card'
import { Button } from '@magnetic/button'
import { Input } from '@magnetic/input'
import { Modal } from '@magnetic/modal'
import { Flex } from '@magnetic/flex'
import { Heading } from '@magnetic/heading'
import { Spinner } from '@magnetic/spinner'
import { Text } from '@magnetic/text'
import { Notification, useToast } from '@magnetic/notification'
import {
  Trash,
  Play,
} from '@/lib/icons'
import { cleanupUnusedImages } from '@/lib/api'
import { invalidateEcrAdminReadModels } from '@/lib/connect-query-invalidation'
import { useMutationWithToast } from '@/hooks/useMutationWithToast'

interface CleanupResult {
  dryRun: boolean
  repositoriesScanned: number
  imagesIdentified: number
  imagesDeleted: number
  imagesFailed: number
  storageBytesIdentified: number
  storageBytesFreed: number
  errors: string[]
  warnings: string[]
  duration: string
}

function formatBytes(bytes: number | bigint | undefined): string {
  if (!bytes) return '0 B'
  const b = typeof bytes === 'bigint' ? Number(bytes) : bytes
  if (b < 1024) return `${b} B`
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`
  if (b < 1024 * 1024 * 1024) return `${(b / (1024 * 1024)).toFixed(1)} MB`
  return `${(b / (1024 * 1024 * 1024)).toFixed(2)} GB`
}

export function ECRCleanupTool() {
  const { addToast } = useToast()

  const [olderThanDays, setOlderThanDays] = useState('30')
  const [excludeTags, setExcludeTags] = useState('latest,production,staging')
  const [maxImages, setMaxImages] = useState('100')
  const [showConfirmDialog, setShowConfirmDialog] = useState(false)
  const [lastResult, setLastResult] = useState<CleanupResult | null>(null)

  const dryRunMutation = useMutationWithToast<{ result?: {
    dryRun: boolean
    repositoriesScanned: number
    imagesIdentified: number
    imagesDeleted: number
    imagesFailed: number
    storageBytesIdentified: bigint
    storageBytesFreed: bigint
    errors: readonly string[]
    warnings: readonly string[]
    duration: string
  } }>(cleanupUnusedImages, {
    successTitle: 'Dry run complete',
    successMessage: (data) => {
      const r = data.result
      if (!r) return ''
      return `Found ${r.imagesIdentified} unused images (${formatBytes(r.storageBytesIdentified)})`
    },
    errorTitle: 'Dry run failed',
    onSuccess: (data) => {
      if (!data.result) return
      const r = data.result
      setLastResult({
        dryRun: r.dryRun,
        repositoriesScanned: r.repositoriesScanned,
        imagesIdentified: r.imagesIdentified,
        imagesDeleted: r.imagesDeleted,
        imagesFailed: r.imagesFailed,
        storageBytesIdentified: Number(r.storageBytesIdentified),
        storageBytesFreed: Number(r.storageBytesFreed),
        errors: [...r.errors],
        warnings: [...r.warnings],
        duration: r.duration,
      })
    },
  })

  const cleanupMutation = useMutationWithToast<{ result?: {
    dryRun: boolean
    repositoriesScanned: number
    imagesIdentified: number
    imagesDeleted: number
    imagesFailed: number
    storageBytesIdentified: bigint
    storageBytesFreed: bigint
    errors: readonly string[]
    warnings: readonly string[]
    duration: string
  } }>(cleanupUnusedImages, {
    successTitle: 'Cleanup complete',
    successMessage: (data) => {
      const r = data.result
      if (!r) return ''
      return `Deleted ${r.imagesDeleted} images, freed ${formatBytes(r.storageBytesFreed)}`
    },
    errorTitle: 'Cleanup failed',
    invalidate: invalidateEcrAdminReadModels,
    onSuccess: (data) => {
      if (!data.result) return
      const r = data.result
      setLastResult({
        dryRun: r.dryRun,
        repositoriesScanned: r.repositoriesScanned,
        imagesIdentified: r.imagesIdentified,
        imagesDeleted: r.imagesDeleted,
        imagesFailed: r.imagesFailed,
        storageBytesIdentified: Number(r.storageBytesIdentified),
        storageBytesFreed: Number(r.storageBytesFreed),
        errors: [...r.errors],
        warnings: [...r.warnings],
        duration: r.duration,
      })
      setShowConfirmDialog(false)
    },
  })

  const handleDryRun = () => {
    dryRunMutation.mutate({
      olderThanDays: parseInt(olderThanDays) || 30,
      excludeTags: excludeTags.split(',').map(t => t.trim()).filter(Boolean),
      maxImagesToDelete: parseInt(maxImages) || 100,
      dryRun: true,
    })
  }

  const handleCleanup = () => {
    if (!lastResult || lastResult.imagesIdentified === 0) {
      addToast({
        status: 'warning',
        title: 'Run dry run first',
        children: 'Please run a dry run to see what will be deleted',
      })
      return
    }
    setShowConfirmDialog(true)
  }

  const confirmCleanup = () => {
    cleanupMutation.mutate({
      olderThanDays: parseInt(olderThanDays) || 30,
      excludeTags: excludeTags.split(',').map(t => t.trim()).filter(Boolean),
      maxImagesToDelete: parseInt(maxImages) || 100,
      dryRun: false,
    })
  }

  return (
    <>
      <Card>
        <Flex direction="vertical" gap={24}>
          {/* Form title and description */}
          <Flex direction="vertical" gap={4}>
            <Flex align="center" gap={8}>
              <Trash size={20} weight="bold" />
              <Heading size="section">Image Cleanup Tool</Heading>
            </Flex>
            <Text color="light">
              Identify and remove unused container images to free up storage space
            </Text>
          </Flex>

          {/* Form fields - single column for better scanability */}
          <Flex direction="vertical" gap={16}>
            <Input
              label="Minimum Age (days)"
              type="number"
              value={olderThanDays}
              onChange={(e) => setOlderThanDays(e.target.value)}
              hint="Only images older than this will be considered"
            />

            <Input
              label="Max Images to Delete"
              type="number"
              value={maxImages}
              onChange={(e) => setMaxImages(e.target.value)}
              hint="Safety limit per cleanup operation"
            />

            <Input
              label="Excluded Tags"
              value={excludeTags}
              onChange={(e) => setExcludeTags(e.target.value)}
              placeholder="latest,production"
              hint="Comma-separated tags to never delete"
            />
          </Flex>

          {/* Buttons - left aligned for full-width form */}
          <Flex gap={8}>
            <Button
              kind="secondary"
              onClick={handleDryRun}
              disabled={dryRunMutation.isPending || cleanupMutation.isPending}
              icon={dryRunMutation.isPending ? <Spinner size="sm" /> : <Play size={16} weight="bold" />}
            >
              Dry Run
            </Button>
            <Button
              destructive
              onClick={handleCleanup}
              disabled={!lastResult || lastResult.imagesIdentified === 0 || cleanupMutation.isPending}
              icon={cleanupMutation.isPending ? <Spinner size="sm" /> : <Trash size={16} weight="bold" />}
            >
              Delete Images
            </Button>
          </Flex>

          {/* Results section */}
          {lastResult && (
            <Flex direction="vertical" gap={16}>
              <Card.Group>
                <Card>
                  <Flex direction="vertical" gap={4}>
                    <Heading size="section">{lastResult.repositoriesScanned}</Heading>
                    <Text size="p4" color="light">Repositories Scanned</Text>
                  </Flex>
                </Card>
                <Card>
                  <Flex direction="vertical" gap={4}>
                    <Heading size="section">{lastResult.imagesIdentified}</Heading>
                    <Text size="p4" color="light">Images Found</Text>
                  </Flex>
                </Card>
                {lastResult.dryRun ? (
                  <Card>
                    <Flex direction="vertical" gap={4}>
                      <Heading size="section">{formatBytes(lastResult.storageBytesIdentified)}</Heading>
                      <Text size="p4" color="light">Would Free</Text>
                    </Flex>
                  </Card>
                ) : (
                  <>
                    <Card>
                      <Flex direction="vertical" gap={4}>
                        <Heading size="section" style={{ color: 'var(--mds-color-theme-text-success-normal)' }}>
                          {lastResult.imagesDeleted}
                        </Heading>
                        <Text size="p4" color="light">Images Deleted</Text>
                      </Flex>
                    </Card>
                    <Card>
                      <Flex direction="vertical" gap={4}>
                        <Heading size="section" style={{ color: 'var(--mds-color-theme-text-success-normal)' }}>
                          {formatBytes(lastResult.storageBytesFreed)}
                        </Heading>
                        <Text size="p4" color="light">Storage Freed</Text>
                      </Flex>
                    </Card>
                  </>
                )}
              </Card.Group>

              {lastResult.warnings.length > 0 && (
                <Notification status="warning" title="Warnings">
                  <Flex direction="vertical" gap={4}>
                    {lastResult.warnings.map((warning, i) => (
                      <Text key={i} size="p3">- {warning}</Text>
                    ))}
                  </Flex>
                </Notification>
              )}

              {lastResult.errors.length > 0 && (
                <Notification status="negative" title="Errors">
                  <Flex direction="vertical" gap={4}>
                    {lastResult.errors.map((error, i) => (
                      <Text key={i} size="p3">- {error}</Text>
                    ))}
                  </Flex>
                </Notification>
              )}

              {!lastResult.dryRun && lastResult.imagesDeleted > 0 && (
                <Notification status="positive" title="Cleanup Successful">
                  <Text size="p3">
                    Deleted {lastResult.imagesDeleted} images in {lastResult.duration}
                  </Text>
                </Notification>
              )}
            </Flex>
          )}
        </Flex>
      </Card>

      <Modal isOpen={showConfirmDialog} onClose={() => setShowConfirmDialog(false)} size="sm">
        <Modal.Content>
          <Flex direction="vertical" gap={24}>
            <Flex direction="vertical" gap={4}>
              <Heading size="section">Confirm Image Deletion</Heading>
              <Text color="light">
                This action cannot be undone. Are you sure you want to delete {lastResult?.imagesIdentified} unused images?
              </Text>
            </Flex>

            <Card>
              <Flex direction="vertical" gap={8}>
                <Flex justify="space-between">
                  <Text size="p3" color="light">Images to delete</Text>
                  <Text size="p3" weight="semi-bold">{lastResult?.imagesIdentified}</Text>
                </Flex>
                <Flex justify="space-between">
                  <Text size="p3" color="light">Storage to free</Text>
                  <Text size="p3" weight="semi-bold">{formatBytes(lastResult?.storageBytesIdentified || 0)}</Text>
                </Flex>
                <Flex justify="space-between">
                  <Text size="p3" color="light">Excluded tags</Text>
                  <Text size="p3" weight="semi-bold">{excludeTags || 'None'}</Text>
                </Flex>
              </Flex>
            </Card>

            <Flex justify="flex-end" gap={8}>
              <Button
                kind="secondary"
                onClick={() => setShowConfirmDialog(false)}
                disabled={cleanupMutation.isPending}
              >
                Cancel
              </Button>
              <Button
                destructive
                onClick={() => confirmCleanup()}
                disabled={cleanupMutation.isPending}
                icon={cleanupMutation.isPending ? <Spinner size="sm" /> : <Trash size={16} weight="bold" />}
              >
                Delete Images
              </Button>
            </Flex>
          </Flex>
        </Modal.Content>
      </Modal>
    </>
  )
}
